/**
 * Buy Box V2 - Supplement Style (Full Section with Gallery)
 * 
 * FUNCTIONALITY:
 * - Gallery navigation (arrows, thumbnails, swipe, keyboard)
 * - Plan selection (single, double, one-time)
 * - Dynamic perk text updates
 * - Add to cart with Skio/Recharge selling plans
 * 
 * CART LOGIC:
 * - One Bottle (Subscribe): quantity 1 + selling plan
 * - Two Bottles (Subscribe): quantity 2 + selling plan  
 * - One-Time Purchase: quantity 1, NO selling plan
 */

(function() {
  'use strict';

  // Initialize all buy boxes on the page
  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('.buy-box-v2').forEach(initBuyBox);
  });

  function initBuyBox(container) {
    initGallery(container);
    initPlanSelection(container);
    initSocialProofScroll(container);
  }

  /**
   * Initialize social proof scroll-to-reviews functionality
   */
  function initSocialProofScroll(container) {
    const socialProof = container.querySelector('.buy-box-v2__social-proof--clickable');
    if (!socialProof) return;

    const reviewsId = socialProof.dataset.reviewsId;
    if (!reviewsId) return;

    socialProof.addEventListener('click', function(e) {
      e.preventDefault();
      const reviewsElement = document.getElementById(reviewsId);
      if (reviewsElement) {
        reviewsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  /**
   * Initialize product gallery functionality
   */
  function initGallery(container) {
    const gallery = container.querySelector('.buy-box-v2__gallery');
    if (!gallery) return;

    const images = gallery.querySelectorAll('.buy-box-v2__gallery-image');
    const thumbnails = gallery.querySelectorAll('.buy-box-v2__thumbnail');
    const prevBtn = gallery.querySelector('.buy-box-v2__gallery-arrow--prev');
    const nextBtn = gallery.querySelector('.buy-box-v2__gallery-arrow--next');
    const counter = gallery.querySelector('.buy-box-v2__gallery-current');
    
    if (images.length === 0) return;

    let currentIndex = 0;

    function showImage(index) {
      // Wrap around
      if (index < 0) index = images.length - 1;
      if (index >= images.length) index = 0;
      
      currentIndex = index;
      
      // Update images
      images.forEach(function(img, i) {
        img.classList.toggle('buy-box-v2__gallery-image--active', i === currentIndex);
      });
      
      // Update thumbnails
      thumbnails.forEach(function(thumb, i) {
        thumb.classList.toggle('buy-box-v2__thumbnail--active', i === currentIndex);
      });
      
      // Update counter
      if (counter) {
        counter.textContent = currentIndex + 1;
      }
    }

    // Arrow navigation
    if (prevBtn) {
      prevBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        showImage(currentIndex - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        showImage(currentIndex + 1);
      });
    }

    // Thumbnail clicks
    thumbnails.forEach(function(thumb) {
      thumb.addEventListener('click', function(e) {
        e.preventDefault();
        const index = parseInt(this.dataset.index, 10);
        showImage(index);
      });
    });

    // Keyboard navigation when gallery is focused
    gallery.addEventListener('keydown', function(e) {
      if (e.key === 'ArrowLeft') {
        showImage(currentIndex - 1);
      } else if (e.key === 'ArrowRight') {
        showImage(currentIndex + 1);
      }
    });

    // Touch swipe support
    let touchStartX = 0;
    let touchEndX = 0;
    
    const galleryMain = gallery.querySelector('.buy-box-v2__gallery-main');
    if (galleryMain) {
      galleryMain.addEventListener('touchstart', function(e) {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      galleryMain.addEventListener('touchend', function(e) {
        touchEndX = e.changedTouches[0].screenX;
        handleSwipe();
      }, { passive: true });
    }

    function handleSwipe() {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;
      
      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          showImage(currentIndex + 1); // Swiped left - next
        } else {
          showImage(currentIndex - 1); // Swiped right - prev
        }
      }
    }
  }

  /**
   * Initialize plan selection and cart functionality
   */
  function initPlanSelection(container) {
    const allPlans = container.querySelectorAll('.buy-box-v2__plan');
    const subscriptionPlans = container.querySelectorAll('.buy-box-v2__plan:not(.buy-box-v2__plan--onetime)');
    const onetimePlan = container.querySelector('.buy-box-v2__plan--onetime');
    const perkWithVariants = container.querySelector('.buy-box-v2__perk[data-perk-single]');
    const primaryCta = container.querySelector('.buy-box-v2__cta--primary');
    
    // Get product data from container data attributes
    const variantId = container.dataset.variantId;
    const sellingPlanId = container.dataset.sellingPlanId;
    const hasSellingPlan = container.dataset.hasSellingPlan === 'true';
    
    let selectedPlan = 'single';
    let selectedQuantity = 1;

    // Helper to update primary CTA opacity based on selection
    function updatePrimaryCtaState() {
      if (primaryCta) {
        const isOnetimeSelected = onetimePlan && onetimePlan.classList.contains('buy-box-v2__plan--selected');
        if (isOnetimeSelected) {
          primaryCta.classList.add('buy-box-v2__cta--faded');
        } else {
          primaryCta.classList.remove('buy-box-v2__cta--faded');
        }
      }
    }

    // Plan selection handler - for subscription plans
    subscriptionPlans.forEach(function(plan) {
      plan.addEventListener('click', function() {
        const planType = this.dataset.plan;
        const quantity = parseInt(this.dataset.quantity, 10) || 1;
        
        // Remove selected state from all plans
        allPlans.forEach(function(p) {
          p.classList.remove('buy-box-v2__plan--selected');
        });
        
        // Add selected state to clicked plan
        this.classList.add('buy-box-v2__plan--selected');
        
        // Update selected plan and quantity
        selectedPlan = planType;
        selectedQuantity = quantity;
        
        // Update primary CTA state (unfade it)
        updatePrimaryCtaState();
        
        // Update dynamic perk text
        if (perkWithVariants) {
          const perkText = planType === 'double' 
            ? perkWithVariants.dataset.perkDouble 
            : perkWithVariants.dataset.perkSingle;
          
          const textSpan = perkWithVariants.querySelector('span');
          if (textSpan && perkText) {
            textSpan.textContent = perkText;
          }
        }
      });
    });

    // One-time plan click handler
    if (onetimePlan) {
      onetimePlan.addEventListener('click', function(e) {
        // Don't toggle if clicking on the inner CTA
        if (e.target.closest('.buy-box-v2__cta--secondary')) {
          return;
        }
        
        // Remove selected state from subscription plans
        subscriptionPlans.forEach(function(p) {
          p.classList.remove('buy-box-v2__plan--selected');
        });
        
        // Toggle one-time selection
        this.classList.add('buy-box-v2__plan--selected');
        selectedPlan = 'onetime';
        selectedQuantity = 1;
        
        // Fade the primary CTA
        updatePrimaryCtaState();
      });

      // One-time CTA click - add to cart WITHOUT selling plan
      const onetimeCta = onetimePlan.querySelector('.buy-box-v2__cta--secondary');
      if (onetimeCta) {
        onetimeCta.addEventListener('click', function(e) {
          e.stopPropagation();
          
          if (variantId) {
            addToCart(variantId, 1, null); // No selling plan
          } else {
            console.error('Buy Box V2: No variant ID found');
          }
        });
      }
    }

    // Primary CTA click handler - add to cart WITH selling plan
    if (primaryCta) {
      primaryCta.addEventListener('click', function(e) {
        e.preventDefault();
        
        // If one-time is selected, clicking primary CTA should switch to first subscription and add
        const isOnetimeSelected = onetimePlan && onetimePlan.classList.contains('buy-box-v2__plan--selected');
        
        if (isOnetimeSelected) {
          // Switch to first subscription plan
          if (subscriptionPlans.length > 0) {
            allPlans.forEach(function(p) {
              p.classList.remove('buy-box-v2__plan--selected');
            });
            subscriptionPlans[0].classList.add('buy-box-v2__plan--selected');
            selectedPlan = subscriptionPlans[0].dataset.plan || 'single';
            selectedQuantity = parseInt(subscriptionPlans[0].dataset.quantity, 10) || 1;
            updatePrimaryCtaState();
          }
          return; // Don't add to cart yet, let them confirm
        }
        
        // Get the currently selected subscription plan
        const selectedSubscription = container.querySelector('.buy-box-v2__plan--selected:not(.buy-box-v2__plan--onetime)');
        
        if (selectedSubscription) {
          const quantity = parseInt(selectedSubscription.dataset.quantity, 10) || 1;
          
          if (variantId) {
            // Add with selling plan if available
            if (hasSellingPlan && sellingPlanId) {
              addToCart(variantId, quantity, sellingPlanId);
            } else {
              // No selling plan available - just add to cart
              console.warn('Buy Box V2: No selling plan found for this product');
              addToCart(variantId, quantity, null);
            }
          } else {
            console.error('Buy Box V2: No variant ID found');
          }
        } else {
          console.warn('Buy Box V2: No subscription plan selected');
        }
      });
    }
  }

  /**
   * Add item to cart via Shopify AJAX API
   * @param {string} variantId - The Shopify variant ID
   * @param {number} quantity - Quantity to add
   * @param {string|null} sellingPlanId - The selling plan ID (for subscriptions) or null for one-time
   */
  function addToCart(variantId, quantity, sellingPlanId) {
    if (!variantId) {
      console.error('Buy Box V2: No variant ID provided');
      return;
    }

    const item = {
      id: parseInt(variantId, 10),
      quantity: quantity
    };

    // Add selling plan if provided (for subscriptions)
    if (sellingPlanId) {
      item.selling_plan = parseInt(sellingPlanId, 10);
    }

    const formData = {
      items: [item]
    };

    // Show loading state
    const ctaButtons = document.querySelectorAll('.buy-box-v2__cta');
    ctaButtons.forEach(function(btn) {
      btn.classList.add('buy-box-v2__cta--loading');
      btn.disabled = true;
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(function(response) {
      if (!response.ok) {
        return response.json().then(function(err) {
          throw new Error(err.message || err.description || 'Failed to add to cart');
        });
      }
      return response.json();
    })
    .then(function(data) {
      console.log('Buy Box V2: Added to cart', data);
      
      // Reset loading state
      ctaButtons.forEach(function(btn) {
        btn.classList.remove('buy-box-v2__cta--loading');
        btn.disabled = false;
      });

      // Try to open cart drawer (multiple theme patterns)
      openCartDrawer();
    })
    .catch(function(error) {
      console.error('Buy Box V2: Error adding to cart', error);
      
      // Reset loading state
      ctaButtons.forEach(function(btn) {
        btn.classList.remove('buy-box-v2__cta--loading');
        btn.disabled = false;
      });

      // Show error to user
      alert('Error: ' + error.message);
    });
  }

  /**
   * Open the cart drawer using various theme patterns
   * Falls back to redirect if no drawer is detected
   */
  function openCartDrawer() {
    // Pattern 1: Shopify standard cart:refresh event (Dawn, etc.)
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    
    // Pattern 2: Open cart drawer event
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('theme:cart:open'));
    
    // Pattern 3: Click the cart icon to trigger drawer (common pattern)
    const cartTriggers = [
      '[data-cart-trigger]',
      '.js-cart-trigger',
      '.cart-icon',
      '[data-action="open-drawer"][data-drawer="cart"]',
      '#cart-icon',
      '.header__icon--cart',
      '[href="/cart"]'
    ];

    let drawerOpened = false;

    for (const selector of cartTriggers) {
      const trigger = document.querySelector(selector);
      if (trigger && typeof trigger.click === 'function') {
        // Check if this looks like it opens a drawer (not a link)
        const isDrawerTrigger = 
          trigger.hasAttribute('data-cart-trigger') ||
          trigger.hasAttribute('data-action') ||
          trigger.classList.contains('js-cart-trigger');
        
        if (isDrawerTrigger) {
          trigger.click();
          drawerOpened = true;
          break;
        }
      }
    }

    // Pattern 4: Check for common cart drawer classes and add open class
    const drawerSelectors = [
      '.cart-drawer',
      '#cart-drawer',
      '.drawer--cart',
      '[data-drawer="cart"]',
      '.mini-cart',
      '.cart-flyout'
    ];

    for (const selector of drawerSelectors) {
      const drawer = document.querySelector(selector);
      if (drawer) {
        drawer.classList.add('is-open', 'active', 'open');
        drawerOpened = true;
        
        // Also try to show overlay if exists
        const overlay = document.querySelector('.drawer-overlay, .cart-overlay');
        if (overlay) {
          overlay.classList.add('is-open', 'active');
        }
        break;
      }
    }

    // If no drawer was found/opened, give a brief delay then redirect
    if (!drawerOpened) {
      setTimeout(function() {
        // Check if drawer is now visible (async themes)
        const visibleDrawer = document.querySelector('.cart-drawer.is-open, .drawer--cart.active, [data-drawer="cart"].open');
        if (!visibleDrawer) {
          // No drawer detected, redirect to cart page
          window.location.href = '/cart';
        }
      }, 300);
    }
  }

})();
